
import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, AlertCircle, CheckCircle, Calendar as CalendarIcon, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { db, auth } from "../firebase/config";
import { collection, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";

interface ExamType {
  id: string;
  name: string;
  weight: number;
  status: "active" | "inactive";
  description?: string;
}

interface ExamSession {
  id: string;
  name: string;
  type: "regular" | "mock" | "custom";
  forms: string[];
  status: "active" | "inactive";
  startDate?: string;
  endDate?: string;
  examTypes: ExamType[];
  year: number;
  submissionAllowed: boolean;
  createdAt?: any;
  updatedAt?: any;
}

const ExamSessionManagement = () => {
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [isCreateSessionOpen, setIsCreateSessionOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [showAutoCreateConfirm, setShowAutoCreateConfirm] = useState(false);
  const [showToggleConfirm, setShowToggleConfirm] = useState<{ sessionId: string; newValue: boolean } | null>(null);
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const [examFormat, setExamFormat] = useState<"cat" | "term">("cat");
  const [classLevels, setClassLevels] = useState<{ level: number; streamCount: number; gradeName: string; convention: string }[]>([]);

  // New session form state
  const [newSession, setNewSession] = useState<Partial<ExamSession>>({
    name: "",
    type: "regular",
    forms: [],
    status: "active",
    examTypes: [],
    year: new Date().getFullYear(),
    submissionAllowed: true,
  });

  // Exam type form state
  const [newExamType, setNewExamType] = useState<Partial<ExamType>>({
    name: "",
    weight: 0,
    status: "active",
    description: "",
  });
  const [editingExamType, setEditingExamType] = useState<{ sessionId: string; examType: ExamType } | null>(null);

  const navigate = useNavigate();
  const { toast } = useToast();

  // Generate available years
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i.toString());
    }
    return years;
  }, []);

  // Authentication logic
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          let selectedAffiliation: { role: string; schoolID?: string } | null = null;
          if (defaultAffiliation && ['teacher', 'school_staff', 'admin'].includes(defaultAffiliation.role) && defaultAffiliation.schoolID) {
            selectedAffiliation = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAffiliation = affiliations.find(aff => ['teacher', 'school_staff', 'admin'].includes(aff.role) && aff.schoolID) || affiliations[0];
          }

          if (selectedAffiliation?.role && ['teacher', 'school_staff', 'admin'].includes(selectedAffiliation.role) && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
            setSchoolID(selectedAffiliation.schoolID);
            setHasValidClaims(true);
            setNetworkError(false);
          } else {
            await new Promise(resolve => setTimeout(resolve, 10000));
            await currentUser.getIdToken(true);
            const retryIdTokenResult = await currentUser.getIdTokenResult(true);
            const retryAffiliations = (retryIdTokenResult.claims.affiliations as { role: string; schoolID?: string }[] | undefined) || [];
            const retryDefaultAff = retryIdTokenResult.claims.defaultAffiliation as { role: string; schoolID?: string } | undefined;
            const retrySchoolID = retryIdTokenResult.claims.schoolID as string | undefined;

            let retrySelectedAffiliation: { role: string; schoolID?: string } | null = null;
            if (retryDefaultAff && ['teacher', 'school_staff', 'admin'].includes(retryDefaultAff.role) && retryDefaultAff.schoolID) {
              retrySelectedAffiliation = retryDefaultAff;
            } else if (retryAffiliations.length > 0) {
              retrySelectedAffiliation = retryAffiliations.find(aff => ['teacher', 'school_staff', 'admin'].includes(aff.role) && aff.schoolID) || retryAffiliations[0];
            }

            if (retrySelectedAffiliation?.role && ['teacher', 'school_staff', 'admin'].includes(retrySelectedAffiliation.role) && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
              setSchoolID(retrySelectedAffiliation.schoolID);
              setHasValidClaims(true);
              setNetworkError(false);
            } else {
              setHasValidClaims(false);
              setSchoolID(null);
              setNetworkError(false);
              toast({
                title: "Permissions Error",
                description: "Invalid permissions to manage exam sessions.",
                variant: "destructive",
              });
            }
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          if (error.code === 'auth/network-request-failed' || error.message.includes('network')) {
            setNetworkError(true);
            toast({
              title: "Network Error",
              description: "Network error occurred. Please check your connection and try again.",
              variant: "destructive",
            });
          } else {
            setHasValidClaims(false);
            setSchoolID(null);
            setNetworkError(false);
            toast({
              title: "Permissions Error",
              description: "Invalid permissions to manage exam sessions.",
              variant: "destructive",
            });
          }
        }
      } else {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        setNetworkError(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch exam sessions with year filter
  useEffect(() => {
    if (!schoolID || isLoading || !hasValidClaims || !user) return;
    const sessionsRef = query(
      collection(db, `schools/${schoolID}/examSessions`),
      where("year", "==", parseInt(selectedYear))
    );
    const unsubscribe = onSnapshot(sessionsRef, (snapshot) => {
      const sessionList: ExamSession[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ExamSession[];
      setSessions(sessionList);
    }, (error) => {
      console.error("Error fetching exam sessions:", error);
      setSessions([]);
      toast({
        title: "Fetch Error",
        description: "Failed to fetch exam sessions. Please try again.",
        variant: "destructive",
      });
    });
    return () => unsubscribe();
  }, [schoolID, isLoading, hasValidClaims, user, selectedYear]);

  // Fetch classes and group by grade name (convention + level)
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;
    
    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(classesRef, (snapshot) => {
      const gradeMap = new Map<string, { level: number; streamCount: number; convention: string }>();
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const level = data.level as number;
        const convention = data.namingConvention as string || "Form";
        const gradeName = `${convention} ${level}`;
        
        if (level && convention) {
          if (gradeMap.has(gradeName)) {
            const existing = gradeMap.get(gradeName)!;
            existing.streamCount += 1;
          } else {
            gradeMap.set(gradeName, { level, streamCount: 1, convention });
          }
        }
      });
      
      const levels = Array.from(gradeMap.entries())
        .map(([gradeName, data]) => ({ 
          gradeName, 
          level: data.level, 
          streamCount: data.streamCount,
          convention: data.convention
        }))
        .sort((a, b) => {
          // Sort by convention first, then by level
          if (a.convention !== b.convention) {
            return a.convention.localeCompare(b.convention);
          }
          return a.level - b.level;
        });
      
      setClassLevels(levels);
    }, (error) => {
      console.error("Error fetching classes:", error);
    });
    
    return () => unsubscribe();
  }, [schoolID, hasValidClaims]);

  const availableFilterTypes = useMemo(() => {
    const types = new Set(sessions.map(s => s.type));
    const options = ['all'];
    if (types.has('regular')) options.push('regular');
    if (types.has('custom')) options.push('custom');
    return options;
  }, [sessions]);

  const calculateTotalWeight = (examTypes: ExamType[]): number => {
    return examTypes.reduce((sum, type) => sum + type.weight, 0);
  };

  const getWeightStatus = (totalWeight: number) => {
    if (totalWeight === 100) return { status: "success", text: "Valid", color: "bg-green-500" };
    if (totalWeight < 100) return { status: "warning", text: `${100 - totalWeight}% remaining`, color: "bg-yellow-500" };
    return { status: "error", text: `${totalWeight - 100}% over`, color: "bg-red-500" };
  };

  const handleCreateSession = async () => {
    if (!schoolID || !newSession.name || !newSession.type || !newSession.year) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      const sessionData: Omit<ExamSession, 'id'> = {
        name: newSession.name,
        type: newSession.type as "regular" | "mock" | "custom",
        forms: newSession.forms || [],
        status: newSession.status as "active" | "inactive" || "active",
        examTypes: [],
        year: newSession.year,
        submissionAllowed: newSession.submissionAllowed ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (newSession.startDate) {
        sessionData.startDate = newSession.startDate;
      }
      if (newSession.endDate) {
        sessionData.endDate = newSession.endDate;
      }

      await addDoc(collection(db, `schools/${schoolID}/examSessions`), sessionData);
      setIsCreateSessionOpen(false);
      setNewSession({
        name: "",
        type: "regular",
        forms: [],
        status: "active",
        examTypes: [],
        year: new Date().getFullYear(),
        submissionAllowed: false,
      });
      toast({
        title: "Success",
        description: "Exam session created successfully.",
        className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800 shadow-sm",
      });
    } catch (error) {
      console.error("Error creating session:", error);
      toast({
        title: "Creation Error",
        description: "Failed to create exam session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAutoCreateSessions = async () => {
    if (!schoolID) return;

    setIsAutoCreating(true);
    try {
      // Delete ALL existing sessions (no year filter) for full wipe
      const sessionsRef = collection(db, `schools/${schoolID}/examSessions`);
      const snapshot = await getDocs(sessionsRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      const termSessions = [
        { name: "TERM 1", type: "regular" as const },
        { name: "TERM 2", type: "regular" as const },
        { name: "TERM 3", type: "regular" as const },
      ];

      // Select exam types based on format
      const examTypesData = examFormat === "cat"
        ? [
            { name: "CAT 1", weight: 15 },
            { name: "CAT 2", weight: 15 },
            { name: "END TERM EXAM", weight: 70 },
          ]
        : [
            { name: "ENTRY EXAM", weight: 20 },
            { name: "MID TERM EXAM", weight: 20 },
            { name: "END TERM EXAM", weight: 60 },
          ];

      for (const term of termSessions) {
        const now = Date.now();
        const examTypesWithIds: ExamType[] = examTypesData.map((et, index) => ({
          id: `${term.name.toLowerCase().replace(/\s+/g, '-')}-${et.name.toLowerCase().replace(/\s+/g, '-')}-${now + index}`,
          name: et.name,
          weight: et.weight,
          status: "active" as const,
        }));

        const baseSessionData: Omit<ExamSession, 'id'> = {
          name: term.name,
          type: term.type,
          forms: classLevels.map(c => c.gradeName),
          status: "active" as const,
          examTypes: examTypesWithIds,
          year: parseInt(selectedYear),
          submissionAllowed: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        await addDoc(collection(db, `schools/${schoolID}/examSessions`), baseSessionData);
      }
      setShowAutoCreateConfirm(false);
      toast({
        title: "Success",
        description: `All sessions wiped and new TERM sessions created for ${selectedYear}.`,
        className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800 shadow-sm",
      });
    } catch (error) {
      console.error("Error auto-creating sessions:", error);
      toast({
        title: "Auto-Create Error",
        description: "Failed to wipe/create sessions. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsAutoCreating(false);
    }
  };

  const handleAddExamType = async (sessionId: string) => {
    if (!schoolID || !newExamType.name || !newExamType.weight || !sessionId) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required exam type fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        toast({
          title: "Session Error",
          description: "Session not found.",
          variant: "destructive",
        });
        return;
      }

      const totalWeight = calculateTotalWeight(session.examTypes) + newExamType.weight;
      if (totalWeight > 100) {
        toast({
          title: "Weight Error",
          description: "Total weight cannot exceed 100%.",
          variant: "destructive",
        });
        return;
      }

      const examType: ExamType = {
        id: `${sessionId}-${Date.now()}`,
        name: newExamType.name,
        weight: newExamType.weight,
        status: newExamType.status as "active" | "inactive" || "active",
        ...(newExamType.description && { description: newExamType.description }),
      };

      const sessionDoc = doc(db, `schools/${schoolID}/examSessions`, sessionId);
      await updateDoc(sessionDoc, {
        examTypes: [...session.examTypes, examType],
        updatedAt: serverTimestamp(),
      });

      setNewExamType({
        name: "",
        weight: 0,
        status: "active",
        description: "",
      });
      toast({
        title: "Success",
        description: "Exam type added successfully to the session.",
        className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-800 dark:text-green-100 dark:border-green-700",
      });
    } catch (error) {
      console.error("Error adding exam type:", error);
      toast({
        title: "Add Error",
        description: "Failed to add exam type. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteExamType = async (sessionId: string, examTypeId: string) => {
    if (!schoolID || !sessionId || !examTypeId) return;

    try {
      const sessionDoc = doc(db, `schools/${schoolID}/examSessions`, sessionId);
      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        toast({
          title: "Session Error",
          description: "Session not found.",
          variant: "destructive",
        });
        return;
      }

      const updatedExamTypes = session.examTypes.filter(et => et.id !== examTypeId);

      await updateDoc(sessionDoc, {
        examTypes: updatedExamTypes,
        updatedAt: serverTimestamp(),
      });
      toast({
        title: "Success",
        description: "Exam type deleted successfully from the session.",
        className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-800 dark:text-green-100 dark:border-green-700",
      });
    } catch (error) {
      console.error("Error deleting exam type:", error);
      toast({
        title: "Delete Error",
        description: "Failed to delete exam type. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateExamType = async () => {
    if (!schoolID || !editingExamType) return;

    try {
      const sessionDoc = doc(db, `schools/${schoolID}/examSessions`, editingExamType.sessionId);
      const session = sessions.find(s => s.id === editingExamType.sessionId);
      if (!session) {
        toast({
          title: "Session Error",
          description: "Session not found.",
          variant: "destructive",
        });
        return;
      }

      // Calculate total weight excluding current exam type
      const otherExamTypesWeight = session.examTypes
        .filter(et => et.id !== editingExamType.examType.id)
        .reduce((sum, et) => sum + et.weight, 0);
      
      if (otherExamTypesWeight + editingExamType.examType.weight > 100) {
        toast({
          title: "Weight Error",
          description: "Total weight cannot exceed 100%.",
          variant: "destructive",
        });
        return;
      }

      const updatedExamTypes = session.examTypes.map(et => 
        et.id === editingExamType.examType.id ? editingExamType.examType : et
      );

      await updateDoc(sessionDoc, {
        examTypes: updatedExamTypes,
        updatedAt: serverTimestamp(),
      });

      setEditingExamType(null);
      toast({
        title: "Success",
        description: "Exam type updated successfully.",
        className: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-100 dark:border-emerald-800 shadow-sm",
      });
    } catch (error) {
      console.error("Error updating exam type:", error);
      toast({
        title: "Update Error",
        description: "Failed to update exam type. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleToggleSubmission = async (sessionId: string, newValue: boolean) => {
    if (!schoolID || !sessionId) return;

    try {
      const sessionDoc = doc(db, `schools/${schoolID}/examSessions`, sessionId);
      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        toast({
          title: "Session Error",
          description: "Session not found.",
          variant: "destructive",
        });
        return;
      }

      await updateDoc(sessionDoc, {
        submissionAllowed: newValue,
        updatedAt: serverTimestamp(),
      });
      toast({
        title: "Marks Registration Updated",
        description: newValue 
          ? `Marks registration enabled for "${session.name}" (${session.year}). Teachers can now submit results for this session's exam types: ${session.examTypes.map(et => et.name).join(", ")}. Ensure the session is active${session.startDate && session.endDate ? ` and within the date range (${session.startDate} to ${session.endDate})` : ""}. Changes saved to Firestore.` 
          : `Marks registration disabled for "${session.name}" (${session.year}). Teachers can no longer submit results for this session. Changes saved to Firestore.`,
        className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-800 dark:text-green-100 dark:border-green-700",
      });
    } catch (error) {
      console.error("Error toggling submission status:", error);
      toast({
        title: "Toggle Error",
        description: "Failed to update marks registration status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleToggleConfirm = (sessionId: string, newValue: boolean) => {
    setShowToggleConfirm({ sessionId, newValue });
  };

  const handleToggleCancel = () => {
    setShowToggleConfirm(null);
  };

  const handleToggleConfirmAction = async () => {
    if (!showToggleConfirm) return;
    await handleToggleSubmission(showToggleConfirm.sessionId, showToggleConfirm.newValue);
    setShowToggleConfirm(null);
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!schoolID || !sessionId) return;

    try {
      await deleteDoc(doc(db, `schools/${schoolID}/examSessions`, sessionId));
      setDeleteSessionId(null);
      toast({
        title: "Success",
        description: "Session deleted successfully.",
        className: "bg-green-100 text-green-800 border-green-200 dark:bg-green-800 dark:text-green-100 dark:border-green-700",
      });
    } catch (error) {
      console.error("Error deleting session:", error);
      toast({
        title: "Delete Error",
        description: "Failed to delete session. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredSessions =
    filterType === "all"
      ? sessions
      : sessions.filter((s) => s.type === filterType);

  const handleFormToggle = (gradeName: string) => {
    const currentForms = newSession.forms || [];
    if (currentForms.includes(gradeName)) {
      setNewSession({
        ...newSession,
        forms: currentForms.filter((f) => f !== gradeName),
      });
    } else {
      setNewSession({
        ...newSession,
        forms: [...currentForms, gradeName],
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (networkError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <p className="text-lg font-semibold text-red-600">Network error occurred. Please check your internet connection and try again.</p>
        <Button 
          onClick={() => { setIsLoading(true); setNetworkError(false); auth.currentUser?.getIdToken(true); }} 
          className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!hasValidClaims) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-lg font-semibold text-red-600">No valid permissions to manage exam sessions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-[#FFFFFF] dark:bg-[#020817] font-plus-jakarta p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground font-source-sans tracking-tight">Exam Session Management</h2>
          <p className="text-sm text-muted-foreground font-plus-jakarta">
            Create and manage exam sessions with multiple exam types
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => setShowAutoCreateConfirm(true)} 
            className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
          >
            <CalendarIcon className="w-3 h-3 mr-2" />
            Auto Create
          </Button>
          <Dialog open={isCreateSessionOpen} onOpenChange={setIsCreateSessionOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent">
                <Plus className="w-3 h-3 mr-2" />
                Create Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] font-inter">
              <DialogHeader className="space-y-2">
                <DialogTitle>Create Exam Session</DialogTitle>
                <DialogDescription>
                  Set up a new exam session with participant classes and exam types
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="session-name" className="font-plus-jakarta font-medium">Session Name</Label>
                    <Input
                      id="session-name"
                      placeholder="e.g., Term 1, Mock 1"
                      className="font-source-sans"
                      value={newSession.name}
                      onChange={(e) =>
                        setNewSession({ ...newSession, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="session-type">Session Type</Label>
                    <Select
                      value={newSession.type}
                      onValueChange={(value) =>
                        setNewSession({ ...newSession, type: value as any })
                      }
                    >
                      <SelectTrigger id="session-type" className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                        <SelectItem value="regular" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Regular</SelectItem>
                        <SelectItem value="custom" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="session-year">Academic Year</Label>
                  <Select
                    value={newSession.year?.toString()}
                    onValueChange={(value) =>
                      setNewSession({ ...newSession, year: parseInt(value) })
                    }
                  >
                    <SelectTrigger id="session-year" className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                      {availableYears.map(year => (
                        <SelectItem
                          key={year}
                          value={year}
                          className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                        >
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {newSession.type === "custom" && (
                  <div className="space-y-3">
                    <Label>Participant Classes</Label>
                    {classLevels.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No classes found. Please add classes first.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {classLevels.map(({ streamCount, gradeName }) => (
                          <div
                            key={gradeName}
                            onClick={() => handleFormToggle(gradeName)}
                            className={`relative cursor-pointer rounded-lg border-2 p-3 transition-all duration-200 ${
                              newSession.forms?.includes(gradeName)
                                ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`form-${gradeName}`}
                                checked={newSession.forms?.includes(gradeName)}
                                onCheckedChange={() => handleFormToggle(gradeName)}
                                className="pointer-events-none"
                              />
                              <div className="flex-1">
                                <label
                                  htmlFor={`form-${gradeName}`}
                                  className={`text-sm font-semibold cursor-pointer ${
                                    newSession.forms?.includes(gradeName)
                                      ? "text-blue-700 dark:text-blue-300"
                                      : "text-gray-900 dark:text-white"
                                  }`}
                                >
                                  {gradeName}
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {streamCount} stream{streamCount !== 1 ? "s" : ""} included
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Start Date (Optional)</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={newSession.startDate}
                      onChange={(e) =>
                        setNewSession({ ...newSession, startDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-date">End Date (Optional)</Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={newSession.endDate}
                      onChange={(e) =>
                        setNewSession({ ...newSession, endDate: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4 px-2 py-2">
                  <Switch
                    id="session-status"
                    checked={newSession.status === "active"}
                    onCheckedChange={(checked) =>
                      setNewSession({
                        ...newSession,
                        status: checked ? "active" : "inactive",
                      })
                    }
                    className="data-[state=checked]:bg-blue-600"
                  />
                  <Label htmlFor="session-status">Active</Label>
                </div>

                <div className="flex items-center space-x-4 px-2 py-2">
                  <Switch
                    id="submission-allowed"
                    checked={newSession.submissionAllowed}
                    onCheckedChange={(checked) =>
                      setNewSession({
                        ...newSession,
                        submissionAllowed: checked,
                      })
                    }
                    className="data-[state=checked]:bg-blue-600"
                  />
                  <Label htmlFor="submission-allowed">Allow Marks Registration</Label>
                </div>

                <Button 
                  onClick={handleCreateSession} 
                  className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
                >
                  Create Session
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Auto Create Confirmation Dialog */}
      <Dialog open={showAutoCreateConfirm} onOpenChange={setShowAutoCreateConfirm}>
        <DialogContent className="max-w-md sm:max-w-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] font-inter">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Confirm Auto Create Sessions
            </DialogTitle>
            <DialogDescription>
              This will delete ALL existing sessions and create TERM 1, TERM 2, TERM 3 for {selectedYear}. Select your preferred exam format below.
            </DialogDescription>
          </DialogHeader>

          {/* Exam Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Select Exam Format</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* CAT System Option */}
              <div
                onClick={() => setExamFormat("cat")}
                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-md ${
                  examFormat === "cat"
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-sm"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                    examFormat === "cat"
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}>
                    {examFormat === "cat" && (
                      <CheckCircle className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-sm ${
                      examFormat === "cat" ? "text-blue-700 dark:text-blue-300" : "text-gray-900 dark:text-white"
                    }`}>
                      CAT System
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Continuous Assessment Tests
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">CAT 1</span>
                        <span className="font-medium text-gray-900 dark:text-white">15%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">CAT 2</span>
                        <span className="font-medium text-gray-900 dark:text-white">15%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">End Term Exam</span>
                        <span className="font-medium text-gray-900 dark:text-white">70%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Term Exam System Option */}
              <div
                onClick={() => setExamFormat("term")}
                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-md ${
                  examFormat === "term"
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-sm"
                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                    examFormat === "term"
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}>
                    {examFormat === "term" && (
                      <CheckCircle className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-semibold text-sm ${
                      examFormat === "term" ? "text-blue-700 dark:text-blue-300" : "text-gray-900 dark:text-white"
                    }`}>
                      Term Exam System
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Entry, Mid-term & End-term
                    </p>
                    <div className="mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">Entry Exam</span>
                        <span className="font-medium text-gray-900 dark:text-white">20%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">Mid Term Exam</span>
                        <span className="font-medium text-gray-900 dark:text-white">20%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600 dark:text-gray-400">End Term Exam</span>
                        <span className="font-medium text-gray-900 dark:text-white">60%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Marks registration will be disabled by default. This action cannot be undone.
          </p>

          <div className="flex gap-3 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAutoCreateConfirm(false)}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAutoCreateSessions}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent relative"
              disabled={isAutoCreating}
            >
              {isAutoCreating ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Creating...
                </div>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toggle Submission Confirmation Dialog */}
      <Dialog open={!!showToggleConfirm} onOpenChange={handleToggleCancel}>
        <DialogContent className="max-w-md sm:max-w-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] font-inter">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Confirm Marks Registration Change
            </DialogTitle>
            <DialogDescription>
              {showToggleConfirm && sessions.find(s => s.id === showToggleConfirm.sessionId)
                ? showToggleConfirm.newValue
                  ? `Enabling marks registration for "${sessions.find(s => s.id === showToggleConfirm.sessionId)!.name}" (${sessions.find(s => s.id === showToggleConfirm.sessionId)!.year}) will allow teachers to submit results for this session's exam types: ${sessions.find(s => s.id === showToggleConfirm.sessionId)!.examTypes.map(et => et.name).join(", ")}. Ensure the session is active${sessions.find(s => s.id === showToggleConfirm.sessionId)!.startDate && sessions.find(s => s.id === showToggleConfirm.sessionId)!.endDate ? ` and within the date range (${sessions.find(s => s.id === showToggleConfirm.sessionId)!.startDate} to ${sessions.find(s => s.id === showToggleConfirm.sessionId)!.endDate})` : ""}. This change will be saved to Firestore.`
                  : `Disabling marks registration for "${sessions.find(s => s.id === showToggleConfirm.sessionId)!.name}" (${sessions.find(s => s.id === showToggleConfirm.sessionId)!.year}) will prevent teachers from submitting results for this session. This change will be saved to Firestore.`
                : "Confirm the marks registration change."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={handleToggleCancel}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={handleToggleConfirmAction}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filter Section */}
      <div className="flex flex-wrap gap-2 p-3">
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-48 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
            <SelectValue placeholder="Select Year" />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
            {availableYears.map(year => (
              <SelectItem
                key={year}
                value={year}
                className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
              >
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
            {availableFilterTypes.map(type => (
              <SelectItem
                key={type}
                value={type}
                className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
              >
                {type.charAt(0).toUpperCase() + type.slice(1)} Sessions
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteSessionId} onOpenChange={() => setDeleteSessionId(null)}>
        <DialogContent className="max-w-md sm:max-w-lg border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] font-inter">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the exam session "{sessions.find(s => s.id === deleteSessionId)?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setDeleteSessionId(null)}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteSession(deleteSessionId!)}
              className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sessions Overview Table */}
      <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
        <CardHeader className="space-y-2">
          <CardTitle className="font-source-sans text-xl font-semibold tracking-tight">Sessions Overview</CardTitle>
          <CardDescription className="font-plus-jakarta text-sm">
            All exam sessions and their configurations for {selectedYear}. Sessions are reset yearly to maintain organization and relevance.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            {filteredSessions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No sessions found for {selectedYear}. Create your first exam session.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] dark:border-[#1E293B] bg-gray-50/50 dark:bg-gray-900/20">
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Session Name</th>
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Type</th>
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Forms</th>
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Total Weight</th>
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Marks Registration</th>
                      <th className="text-left p-3 font-plus-jakarta font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map((session) => {
                      const totalWeight = calculateTotalWeight(session.examTypes);
                      const weightStatus = getWeightStatus(totalWeight);
                      return (
                        <tr key={session.id} className="border-b border-[#E2E8F0] dark:border-[#1E293B] hover:bg-muted/50">
                          <td className="p-3 font-medium">{session.name}</td>
                          <td className="p-3">
                            <Badge 
                              variant="outline" 
                              className={`
                                capitalize font-plus-jakarta text-sm py-1 px-3
                                ${session.type === "regular" 
                                  ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800" 
                                  : "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800"
                                }
                              `}
                            >
                              {session.type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              {session.forms.map((form) => (
                                <Badge 
                                  key={form} 
                                  variant="secondary" 
                                  className="text-xs bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800"
                                >
                                  Form {form}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-between w-full gap-2">
                              <span className="font-plus-jakarta font-medium text-gray-800 dark:text-gray-200">{totalWeight}%</span>
                              <Badge
                                variant={
                                  weightStatus.status === "success"
                                    ? "default"
                                    : weightStatus.status === "warning"
                                    ? "secondary"
                                    : "destructive"
                                }
                                className={`text-sm font-medium font-plus-jakarta flex items-center gap-1.5 px-3 py-1 ${
                                  weightStatus.status === "success"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
                                    : weightStatus.status === "warning"
                                    ? "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-800"
                                    : "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800"
                                }`}
                              >
                                {weightStatus.status === "success" ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  <AlertCircle className="w-4 h-4" />
                                )}
                                {weightStatus.text}
                              </Badge>
                            </div>
                          </td>
                          <td className="p-3">
                            <Switch
                              checked={session.submissionAllowed}
                              onCheckedChange={(newValue) => handleToggleConfirm(session.id, newValue)}
                              className="data-[state=checked]:bg-blue-600"
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-md sm:max-w-4xl max-h-[90vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] font-inter">
                                  <DialogHeader className="space-y-2">
                                    <DialogTitle>
                                      Edit Exam Types - {session.name} ({session.year})
                                    </DialogTitle>
                                    <DialogDescription>
                                      Configure exam types and their weights (must sum to 100%)
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    <div className="space-y-4">
                                      <h4 className="font-medium m-2">Current Exam Types</h4>
                                      {session.examTypes.length === 0 ? (
                                        <Alert className="p-4 m-2">
                                          <AlertCircle className="h-4 w-4" />
                                          <AlertDescription>
                                            No exam types configured yet. Add exam types below.
                                          </AlertDescription>
                                        </Alert>
                                      ) : (
                                        <div className="space-y-3">
                                          {session.examTypes.map((examType) => (
                                            <div
                                              key={examType.id}
                                              className="flex flex-col sm:flex-row sm:items-center justify-between p-4 m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817] font-inter shadow-sm gap-3"
                                            >
                                              {editingExamType?.examType.id === examType.id ? (
                                                /* Edit Mode */
                                                <div className="flex-1 space-y-3">
                                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                    <div className="space-y-1">
                                                      <Label className="text-xs">Name</Label>
                                                      <Input
                                                        value={editingExamType.examType.name}
                                                        onChange={(e) => setEditingExamType({
                                                          ...editingExamType,
                                                          examType: { ...editingExamType.examType, name: e.target.value }
                                                        })}
                                                        className="h-9"
                                                      />
                                                    </div>
                                                    <div className="space-y-1">
                                                      <Label className="text-xs">Weight (%)</Label>
                                                      <Input
                                                        type="number"
                                                        min="1"
                                                        max="100"
                                                        value={editingExamType.examType.weight}
                                                        onChange={(e) => setEditingExamType({
                                                          ...editingExamType,
                                                          examType: { ...editingExamType.examType, weight: parseInt(e.target.value) || 0 }
                                                        })}
                                                        className="h-9"
                                                      />
                                                    </div>
                                                    <div className="space-y-1">
                                                      <Label className="text-xs">Status</Label>
                                                      <Select
                                                        value={editingExamType.examType.status}
                                                        onValueChange={(value: "active" | "inactive") => setEditingExamType({
                                                          ...editingExamType,
                                                          examType: { ...editingExamType.examType, status: value }
                                                        })}
                                                      >
                                                        <SelectTrigger className="h-9">
                                                          <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                          <SelectItem value="active">Active</SelectItem>
                                                          <SelectItem value="inactive">Inactive</SelectItem>
                                                        </SelectContent>
                                                      </Select>
                                                    </div>
                                                  </div>
                                                  <div className="flex gap-2 mt-3">
                                                    <Button
                                                      size="sm"
                                                      onClick={handleUpdateExamType}
                                                      className="bg-emerald-600 hover:bg-emerald-700 text-white mt-1"
                                                    >
                                                      Save
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      onClick={() => setEditingExamType(null)}
                                                      className="mt-1"
                                                    >
                                                      Cancel
                                                    </Button>
                                                  </div>
                                                </div>
                                              ) : (
                                                /* View Mode */
                                                <>
                                                  <div className="flex-1 space-y-1">
                                                    <p className="font-medium">
                                                      {examType.name}
                                                    </p>
                                                    {examType.description && (
                                                      <p className="text-sm text-muted-foreground">
                                                        {examType.description}
                                                      </p>
                                                    )}
                                                  </div>
                                                  <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                                                    <Badge variant="outline" className={`font-plus-jakarta text-sm py-1 px-3 flex items-center gap-2 transition-colors ${
                                                      weightStatus.status === "success"
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100/80 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/40"
                                                        : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100/80 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-900/40"
                                                      }`}>
                                                      {examType.weight}%
                                                    </Badge>
                                                    <Badge
                                                      variant={examType.status === "active" ? "default" : "outline"}
                                                      className={examType.status === "active" 
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
                                                        : "text-gray-600 dark:text-gray-400"}
                                                    >
                                                      <div className="flex items-center gap-1">
                                                        {examType.status === "active" && (
                                                          <CheckCircle className="h-3 w-3 text-emerald-500 dark:text-emerald-400" />
                                                        )}
                                                        {examType.status}
                                                      </div>
                                                    </Badge>
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      className="px-2 py-1 mt-1"
                                                      onClick={() => setEditingExamType({ sessionId: session.id, examType: { ...examType } })}
                                                    >
                                                      <Edit className="w-3 h-3" />
                                                    </Button>
                                                    <Button
                                                      size="sm"
                                                      variant="outline"
                                                      className="px-2 py-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 mt-1"
                                                      onClick={() =>
                                                        handleDeleteExamType(
                                                          session.id,
                                                          examType.id
                                                        )
                                                      }
                                                    >
                                                      <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    <div className={`w-full p-4 m-2 rounded-lg border ${
                                        weightStatus.status === "success"
                                          ? "bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                                          : "bg-red-50/50 border-red-200 dark:bg-red-950/20 dark:border-red-800"
                                      }`}>
                                      <div className="flex items-center gap-2">
                                        {weightStatus.status === "success" ? (
                                          <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400" />
                                        ) : (
                                          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                                        )}
                                        <span className={`font-medium ${
                                          weightStatus.status === "success"
                                            ? "text-emerald-700 dark:text-emerald-300"
                                            : "text-red-700 dark:text-red-300"
                                        }`}>
                                          <strong>Total Weight: {totalWeight}%</strong> - {weightStatus.text}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg p-4 m-2 space-y-4 bg-[#FFFFFF] dark:bg-[#020817] font-inter shadow-sm">
                                      <h4 className="font-medium">Add Exam Type</h4>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Exam Type Name</Label>
                                          <Input
                                            placeholder="e.g., CAT 1, Midterm"
                                            value={newExamType.name}
                                            onChange={(e) =>
                                              setNewExamType({
                                                ...newExamType,
                                                name: e.target.value,
                                              })
                                            }
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Weight (%)</Label>
                                          <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            placeholder="e.g., 20"
                                            value={newExamType.weight || ""}
                                            onChange={(e) =>
                                              setNewExamType({
                                                ...newExamType,
                                                weight: parseInt(e.target.value) || 0,
                                              })
                                            }
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-2 p-4 rounded-lg bg-gray-50/50 dark:bg-gray-900/30">
                                        <Label className="text-gray-700 dark:text-gray-300">Description (Optional)</Label>
                                        <Textarea
                                          placeholder="e.g., Midterm covers 1st half of term content"
                                          value={newExamType.description}
                                          onChange={(e) =>
                                            setNewExamType({
                                              ...newExamType,
                                              description: e.target.value,
                                            })
                                          }
                                          className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                                        />
                                      </div>
                                      <div className="flex items-center space-x-4 p-4 m-2">
                                        <Switch
                                          id="exam-type-status"
                                          checked={newExamType.status === "active"}
                                          onCheckedChange={(checked) =>
                                            setNewExamType({
                                              ...newExamType,
                                              status: checked ? "active" : "inactive",
                                            })
                                          }
                                          className="data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-gray-200"
                                        />
                                        <Label htmlFor="exam-type-status">Active</Label>
                                      </div>
                                      <Button
                                        onClick={() => handleAddExamType(session.id)}
                                        className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB] hover:border-blue-300 border border-transparent"
                                        disabled={
                                          !newExamType.name ||
                                          !newExamType.weight ||
                                          totalWeight + (newExamType.weight || 0) > 100
                                        }
                                      >
                                        <Plus className="w-3 h-3 mr-2" />
                                        Add Exam Type
                                      </Button>
                                      {totalWeight + (newExamType.weight || 0) > 100 && (
                                        <p className="text-sm text-destructive">
                                          Adding this exam type would exceed 100% total weight
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <Button
                                size="sm"
                                variant="destructive"
                                className="w-full sm:w-auto min-w-[80px] px-3 py-1 text-sm font-semibold hover:bg-destructive/90"
                                onClick={() => setDeleteSessionId(session.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <Toaster />
    </div>
  );
};

export default ExamSessionManagement;
